import React from 'react';
import { Box, Typography, Container, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../layout';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Logo />
          <Button onClick={() => navigate(-1)} sx={{ ml: 'auto' }}>
            Back
          </Button>
        </Box>

        <Typography variant="h4" component="h1" gutterBottom>
          Privacy Policy
        </Typography>

        <Typography variant="body1" paragraph>
          Last updated: February 19, 2026
        </Typography>

        <Typography variant="body1" paragraph>
          This Privacy Policy ("Policy") describes how NanoTradingCopilot ("we," "us," or "our") collects, uses, stores, and discloses information in connection with your use of the NanoTradingCopilot service (the "Service"). It applies whenever you visit our website, use the Service, or otherwise interact with us. By using the Service, you agree to the collection and use of your information as described in this Policy, and you consent to all actions taken by us with respect to your information as outlined herein.
        </Typography>

        <Typography variant="body1" paragraph>
          If you do not agree to any part of this Policy, please discontinue your use of the Service. This Policy is incorporated into, and is subject to, our Terms of Service.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          1. Information We Collect
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          1.1 Personal Information
        </Typography>
        <Typography variant="body1" paragraph>
          Account Registration Data: When you create an account or subscribe to the Service, we may collect information such as your name, email address, billing address, payment details, and other contact information necessary to process your subscription and provide the Service.
        </Typography>
        <Typography variant="body1" paragraph>
          Communication Data: If you contact us (e.g., through email or support channels), we collect and store your communication and any related information (like your name, email address, and the substance of your inquiry).
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          1.2 Payment Information
        </Typography>
        <Typography variant="body1" paragraph>
          We use Stripe, a third-party payment processor, to handle your payment transactions. We do not directly collect or store your full credit card or banking information. Payment details are handled and stored by Stripe in accordance with their privacy policies and terms.
        </Typography>
        <Typography variant="body1" paragraph>
          Stripe may collect, use, and store your payment information as necessary for processing transactions, preventing fraud, and complying with their legal obligations. If you have any questions about how Stripe handles your personal information, please review Stripe's Privacy Policy at{' '}
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
            https://stripe.com/privacy
          </a>{' '}
          or contact Stripe directly.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          1.3 Usage Data
        </Typography>
        <Typography variant="body1" paragraph>
          As stated in our Terms of Service, we collect and generate performance, analytical, or usage data relating to your access or use of the Service ("Usage Data"). Usage Data may include:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Log Information: IP addresses, browser type, operating system, access times, pages viewed, and referral URLs.</Typography>
          <Typography component="li">Service Interactions: actions taken within the Service (e.g., clicks, feature usage, settings adjustments).</Typography>
          <Typography component="li">Error and Debugging Information: crash reports, error logs, and other diagnostic data.</Typography>
        </Box>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          1.4 User Content
        </Typography>
        <Typography variant="body1" paragraph>
          If you submit, post, or display any content on or through the Service ("User Content"), we may collect and store that content for the purposes described in our Terms of Service and in this Policy.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          1.5 Alpha Testing Data
        </Typography>
        <Typography variant="body1" paragraph>
          Because the Service is currently in Alpha stage, we collect expanded data for debugging, improvement, and development purposes, including:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Performance metrics tied to your usage</Typography>
          <Typography component="li">Error logs and crash reports</Typography>
          <Typography component="li">System diagnostics</Typography>
          <Typography component="li">Feedback and suggestions you provide (voluntarily or upon request)</Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          2. How We Use Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We use the information we collect for various purposes, including:
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          2.1 To Provide and Improve the Service
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Operate and maintain the Service (including Alpha-stage features)</Typography>
          <Typography component="li">Monitor performance and stability, identify bugs or errors, and address technical issues</Typography>
          <Typography component="li">Process subscription fees and manage billing information</Typography>
        </Box>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          2.2 To Communicate With You
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Respond to your inquiries, requests, or support tickets</Typography>
          <Typography component="li">Provide updates, security alerts, and administrative messages</Typography>
          <Typography component="li">Send marketing or promotional communications (you can opt out at any time)</Typography>
        </Box>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          2.3 Analytics and Product Development
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Analyze Usage Data to understand how users engage with the Service</Typography>
          <Typography component="li">Improve our AI algorithms, user interface, and overall functionality</Typography>
          <Typography component="li">Develop new features, products, or services</Typography>
        </Box>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          2.4 Legal and Compliance
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Enforce our Terms of Service and other agreements</Typography>
          <Typography component="li">Comply with applicable laws, regulations, or court orders</Typography>
          <Typography component="li">Detect, investigate, or prevent fraud or other unauthorized activity</Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          3. Sharing and Disclosure of Information
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          3.1 Service Providers
        </Typography>
        <Typography variant="body1" paragraph>
          We may share your information with third-party service providers or vendors who perform functions on our behalf, such as payment processing (Stripe), hosting, analytics, or customer support. These providers are contractually obligated to protect your information and only use it as necessary to provide services to us.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          3.2 Corporate Transactions
        </Typography>
        <Typography variant="body1" paragraph>
          If we are involved in a merger, acquisition, financing, or sale of assets, your information may be transferred as part of that transaction. We will endeavor to ensure the receiving entity follows this Policy or provides you with notice and choice before transferring your information.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          3.3 Legal Obligations
        </Typography>
        <Typography variant="body1" paragraph>
          We may disclose your information if required by law, court order, or governmental regulation, or if we believe such disclosure is necessary to:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Protect our rights, property, or safety</Typography>
          <Typography component="li">Investigate fraud or respond to a legal request</Typography>
          <Typography component="li">Comply with law enforcement or other legal processes</Typography>
        </Box>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          3.4 With Your Consent
        </Typography>
        <Typography variant="body1" paragraph>
          We may share your information for other purposes not specifically listed here if you consent to such disclosure.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          4. Cookies, Local Storage, and Analytics
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          4.1 Firebase Analytics
        </Typography>
        <Typography variant="body1" paragraph>
          We use Google Firebase Analytics to understand how users interact with the Service. Firebase Analytics collects data such as page views, feature usage events (e.g., login, signup, theme changes), and device/browser information. This data is sent to Google and processed in accordance with Google's Privacy Policy. Firebase Analytics may set cookies or use similar technologies to identify unique users across sessions. You may opt out of analytics tracking by declining non-essential cookies when prompted, or by using browser extensions such as the Google Analytics Opt-out Browser Add-on.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          4.2 Local Storage
        </Typography>
        <Typography variant="body1" paragraph>
          We use your browser's local storage (not cookies) to store the following functional preferences locally on your device:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Theme preference (dark/light mode)</Typography>
          <Typography component="li">UI layout state (panel collapsed/expanded)</Typography>
          <Typography component="li">Recently viewed trading instruments</Typography>
        </Box>
        <Typography variant="body1" paragraph>
          This data is stored only on your device and is not transmitted to our servers. You can clear this data at any time through your browser settings.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          4.3 Essential Cookies
        </Typography>
        <Typography variant="body1" paragraph>
          Firebase Authentication may set cookies necessary for maintaining your login session. These are strictly necessary for the Service to function and cannot be disabled while using the Service.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          4.4 Managing Cookies
        </Typography>
        <Typography variant="body1" paragraph>
          You can manage or disable non-essential cookies through your browser settings or through our cookie consent notice. Disabling essential cookies may prevent you from using the Service. Disabling analytics cookies will not affect the functionality of the Service.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          5. Data Security
        </Typography>
        <Typography variant="body1" paragraph>
          We implement commercially reasonable security measures to help protect the information we collect and store. However, no system is 100% secure, and we cannot guarantee the absolute security of your information. Please take steps to protect your account credentials and promptly notify us if you suspect any unauthorized access or breach of your account.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          6. Data Retention
        </Typography>
        <Typography variant="body1" paragraph>
          We retain your personal information only for as long as necessary to:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Fulfill the purposes outlined in this Policy</Typography>
          <Typography component="li">Comply with legal obligations (e.g., record-keeping requirements)</Typography>
          <Typography component="li">Resolve disputes and enforce our agreements</Typography>
        </Box>
        <Typography variant="body1" paragraph>
          We may retain Usage Data or aggregated/anonymized data for longer periods for research, development, or business analytics, provided it does not identify any individual user.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          7. Google Authentication and User Data
        </Typography>

        <Typography variant="body1" paragraph>
          Our Service offers Google Sign-In as an authentication option. When you choose to sign in with your Google account, we collect certain information from your Google account as described below.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          7.1 Google User Data We Collect
        </Typography>
        <Typography variant="body1" paragraph>
          When you sign in with Google, we may collect the following information from your Google account:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Your Google account email address</Typography>
          <Typography component="li">Your Google profile information (such as your name and profile picture)</Typography>
          <Typography component="li">Your Google account ID (for authentication purposes)</Typography>
        </Box>
        <Typography variant="body1" paragraph>
          We do not request or access any other Google account data beyond what is necessary for authentication and basic profile information.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          7.2 How We Use Google User Data
        </Typography>
        <Typography variant="body1" paragraph>
          We use Google user data strictly for the following purposes:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">To authenticate your identity and provide access to our Service</Typography>
          <Typography component="li">To create and maintain your user account within our Service</Typography>
          <Typography component="li">To personalize your experience (such as displaying your name and profile picture)</Typography>
          <Typography component="li">To communicate with you about your account and our Service</Typography>
        </Box>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          7.3 Limited Use Disclosure
        </Typography>
        <Typography variant="body1" paragraph>
          Our use of information received from Google APIs will adhere to Google's Limited Use Requirements. Specifically:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">We will only use Google user data to provide or improve user-facing features that are prominent in the requesting application's user interface.</Typography>
          <Typography component="li">We will not use Google user data for serving advertisements.</Typography>
          <Typography component="li">We will not allow humans to read Google user data unless: (a) we have obtained the user's affirmative consent for specific content; (b) it is necessary for security or abuse prevention; (c) it is necessary to comply with applicable laws; or (d) it is necessary for internal operations, and the data is aggregated and anonymized.</Typography>
          <Typography component="li">We will not transfer Google user data to others unless doing so is necessary to provide or improve user-facing features that are prominent in the requesting application's user interface, and then only with adequate privacy protections in place.</Typography>
        </Box>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          7.4 Retention and Deletion of Google User Data
        </Typography>
        <Typography variant="body1" paragraph>
          We retain Google user data only for as long as necessary to provide our Service to you. If you choose to disconnect your Google account from our Service or delete your account entirely, we will delete your Google user data from our active systems within 30 days, except where retention is necessary to comply with legal obligations, resolve disputes, or enforce our agreements.
        </Typography>
        <Typography variant="body1" paragraph>
          To request deletion of your Google user data, you can:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Delete your account through the account settings in our Service</Typography>
          <Typography component="li">Contact us directly at support@nanotradingcopilot.com with your deletion request</Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          8. AI Services and Third-Party Data Processing
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          8.1 OpenAI
        </Typography>
        <Typography variant="body1" paragraph>
          Certain features of the Service (such as AI-powered chat and AI validation rules) use OpenAI's API to process your requests. When you use these features, the following data may be sent to OpenAI for processing:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Your chat messages and conversation history</Typography>
          <Typography component="li">Trade validation inputs (instrument, entry, stop, target)</Typography>
          <Typography component="li">Market data context (price data, technical indicators, economic calendar events)</Typography>
          <Typography component="li">Your trading preferences and account settings relevant to the conversation</Typography>
        </Box>
        <Typography variant="body1" paragraph>
          OpenAI processes this data in accordance with their API usage policies. Data sent via the API is not used by OpenAI to train their models. For more information, see OpenAI's Privacy Policy at{' '}
          <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">
            https://openai.com/policies/privacy-policy
          </a>.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          8.2 Market Data Providers
        </Typography>
        <Typography variant="body1" paragraph>
          We use third-party market data providers (including OANDA, Yahoo Finance, Polygon.io, and Binance) to fetch price data and market information. These requests contain instrument identifiers and timeframes only — no personal user data is shared with market data providers.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          9. Your Rights and Choices
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          9.1 Account Information
        </Typography>
        <Typography variant="body1" paragraph>
          You can access, update, or delete certain personal information within your account settings. If you wish to terminate your account, please follow the instructions in our Terms of Service or contact us. Upon request, we will take reasonable steps to remove your personal information from active databases, but we may retain certain information to comply with legal obligations or for legitimate business purposes.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          9.2 Marketing Emails
        </Typography>
        <Typography variant="body1" paragraph>
          If you receive marketing emails from us, you can opt out at any time by following the instructions at the bottom of those emails or by contacting us.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          9.3 Cookies and Analytics
        </Typography>
        <Typography variant="body1" paragraph>
          You can manage analytics and non-essential cookies through our cookie consent notice or your browser settings. See Section 4 for details on what cookies and local storage we use. Disabling essential cookies may prevent you from using the Service.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          9.4 Do Not Track
        </Typography>
        <Typography variant="body1" paragraph>
          Our Service does not currently respond to "Do Not Track" signals in browsers. However, you may opt out of analytics tracking as described in Section 4.1.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          10. International Users
        </Typography>
        <Typography variant="body1" paragraph>
          Although the Service is intended for users located within Canada, if you choose to access or use the Service from outside Canada, you acknowledge that your information may be stored and processed in Canada or other jurisdictions, where data protection laws may differ from those in your region.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          11. Children's Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          The Service is not intended for individuals under the age of majority (as defined in our Terms of Service) or anyone under 18 years old. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us, and we will take steps to delete such information.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          12. Changes to This Privacy Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We may update this Policy from time to time to reflect changes in our practices or legal requirements. When we do, we will revise the "Last updated" date at the top of this page and post the updated Policy on our website. Your continued use of the Service after any modifications to this Policy indicates your acceptance of the updated terms.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          13. Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have any questions or concerns about this Privacy Policy, or if you wish to exercise any of your rights regarding your personal information, please contact us at:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Email: support@nanotradingcopilot.com</Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy; 